import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { resolveBackendUrl } from '../../services/apiConfig';

const normalizePhoneFriendlyUrl = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    if (['localhost', '127.0.0.1', '0.0.0.0'].includes(parsed.hostname)) {
      parsed.hostname = window.location.hostname;
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

export default function MyQRCode() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/accounts/profile/').then(res => setProfile(res.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex justify-center mt-20">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0B5D1E]/20 border-t-[#0B5D1E]" />
    </div>
  );

  const qrUrl = profile?.qr_code
    ? normalizePhoneFriendlyUrl(
        profile.qr_code.startsWith('http') ? profile.qr_code : resolveBackendUrl(profile.qr_code),
      )
    : null;
  // Always point to the React frontend so phones can open it
  const verifyUrl = profile?.public_profile_slug
    ? `${window.location.origin}/verify/${profile.public_profile_slug}/`
    : null;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>My QR Code</h1>
        <p className="text-sm text-gray-500 mt-1">Your personal member verification QR code</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-5 text-center">
        {qrUrl ? (
          <>
            <div className="bg-gray-50 inline-block p-4 rounded-xl mb-4">
              <a href={verifyUrl || qrUrl} target="_blank" rel="noopener noreferrer" className="block">
                <img src={qrUrl} alt="Your QR Code" className="w-44 h-44" />
              </a>
            </div>
            <p className="text-gray-600 mb-2 text-sm">
              Scan this QR code to verify your membership.
            </p>
            <p className="text-xs text-gray-400 mb-5">
              When scanned, it shows your passport photo, name, company name, SURCON number, NIS status, shortcode, email, and phone.
            </p>
            <a
              href={qrUrl}
              download={`NIS_QR_${profile?.membership_number || 'member'}.png`}
              className="inline-flex items-center justify-center gap-2 bg-[#0B5D1E] text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-[#062E10] transition text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              Download QR Code
            </a>
            {verifyUrl && (
              <div className="mt-4 space-y-2">
                <a
                  href={verifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#0B5D1E] hover:text-[#062E10] underline text-sm font-medium"
                >
                  Open Verification Page
                </a>
                <p className="text-xs text-gray-400 break-all">{verifyUrl}</p>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="w-64 h-64 mx-auto bg-gray-50 rounded-xl flex flex-col items-center justify-center mb-6 border-2 border-dashed border-gray-200">
              <svg className="w-16 h-16 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
              <p className="text-sm text-gray-400">No QR code yet</p>
            </div>
            <p className="text-gray-500 mb-2 text-sm">Your QR code hasn't been generated yet.</p>
            <p className="text-xs text-gray-400">Please contact an administrator to generate your QR code.</p>
          </>
        )}
      </div>

      <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
        <h3 className="font-semibold text-blue-900 mb-2 text-sm">About Your QR Code</h3>
        <ul className="text-xs text-blue-700 space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            Your QR code links to a verification page showing your membership details
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            Anyone scanning it can verify your name, company, SURCON number, and status
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            Download and print it on your business card or ID
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">•</span>
            QR codes are generated and managed by the admin
          </li>
        </ul>
      </div>
    </div>
  );
}
